import { useMemo, useState } from "react";
import { generateRecoveryPhrase, keypairFromSecret, phraseWords, publicKeyToNpub, secretKeyFromPhrase } from "@timecapsule/crypto";
import { navigate } from "../router";
import { useKeyOwner } from "../members";
import { useSession } from "../session";
import { relinkLogin } from "../keys";
import { errorText, useT } from "../i18n";
import { PhraseInput, ProtectKey } from "./KeySetup";

type Step = "intro" | "announce" | "show" | "confirm" | "enter" | "protect";

/** Three distinct word positions the member must type back. */
function pickChecks(): number[] {
  const picked = new Set<number>();
  while (picked.size < 3) picked.add(crypto.getRandomValues(new Uint32Array(1))[0] % 12);
  return [...picked].sort((a, b) => a - b);
}

/** The optional key step right after sign-up (PLAN.md 4.16 K1): create the key now or skip. */
export function Onboarding({ onSkip }: { onSkip: () => void }) {
  const t = useT();
  const { me } = useSession();
  return (
    <div className="narrow">
      <h1>{t.onboarding.welcome(me.display_name)}</h1>
      {/* The first key leads to Try it, the guided first capsule (reviews/06-action-plan.md 4.1). */}
      <KeyFlow mode="signup" onSkip={onSkip} onDone={() => navigate("/try")} />
    </div>
  );
}

/**
 * Making a key (PLAN.md 4.16 K1), the same flow at sign-up and under Account -> Keys: what a key
 * is, an announced phrase shown only on request, three words typed back, PIN + passkey, then the
 * member confirms it is them and the key is registered. `account` mode also offers a phrase the
 * member already has (another account's: join it; a new one: use it). `onDone` gets the new key.
 */
export function KeyFlow({ mode, onSkip, onDone }: { mode: "signup" | "account" | "reset"; onSkip?: () => void; onDone: (secretKey: Uint8Array) => void }) {
  const t = useT();
  const { me } = useSession();
  const [step, setStep] = useState<Step>("intro");
  const [phrase, setPhrase] = useState<string | null>(null);
  const [secretKey, setSecretKey] = useState<Uint8Array | null>(null);
  // A phrase typed here whose key belongs to another member: that member's account.
  const owner = useKeyOwner(secretKey ? keypairFromSecret(secretKey).publicKey : null);

  function startNew() {
    const p = generateRecoveryPhrase();
    setPhrase(p);
    setSecretKey(secretKeyFromPhrase(p));
    setStep("announce");
  }

  return (
    <>
      {step === "intro" && (
        <>
          <h2>{t.keyFlow.title}</h2>
          <ul className="plain">
            <li>{t.keyFlow.signature}</li>
            <li>{t.onboarding.phraseRole}</li>
            <li>{t.keyFlow.whoever}</li>
            <li>{t.onboarding.googleRole}</li>
          </ul>
          <div className="actions-row">
            <button className="primary" onClick={startNew}>
              {mode === "reset" ? t.keyFlow.createNew : t.onboarding.createKey}
            </button>
            {mode === "account" && <button onClick={() => setStep("enter")}>{t.onboarding.havePhrase}</button>}
            {onSkip && <button onClick={onSkip}>{t.keyFlow.skip}</button>}
          </div>
          {onSkip && <p className="small muted">{t.keyFlow.skipNote}</p>}
        </>
      )}

      {step === "announce" && (
        <>
          <h2>{t.onboarding.phraseTitle}</h2>
          <div className="warn">
            <p>{t.keyFlow.announce}</p>
            <ul>
              {t.keyFlow.announceChecks.map((line) => <li key={line}>{line}</li>)}
            </ul>
          </div>
          <button className="primary" onClick={() => setStep("show")}>{t.keyFlow.showPhrase}</button>
        </>
      )}

      {step === "show" && phrase && <ShowPhrase phrase={phrase} onNext={() => setStep("confirm")} />}
      {step === "confirm" && phrase && <ConfirmPhrase phrase={phrase} onBack={() => setStep("show")} onNext={() => setStep("protect")} />}

      {step === "enter" && (
        <>
          <h2>{t.onboarding.phraseTitle}</h2>
          <p>{t.onboarding.typePhrase}</p>
          <PhraseInput onKey={setSecretKey} />
          {secretKey && <p className="small">{t.onboarding.phraseIs(publicKeyToNpub(keypairFromSecret(secretKey).publicKey))}</p>}
          {secretKey && owner ? (
            <Relink secretKey={secretKey} ownerName={owner.display_name} />
          ) : (
            <button className="primary" disabled={!secretKey} onClick={() => setStep("protect")}>
              {t.common.continue}
            </button>
          )}{" "}
          <button className="link" onClick={() => setStep("intro")}>
            {t.common.back}
          </button>
        </>
      )}

      {step === "protect" && secretKey && <ProtectKey secretKey={secretKey} serverCopy onDone={() => onDone(secretKey)} />}
    </>
  );
}

/**
 * Join that account (today's re-link, PLAN.md 4.6): the phrase proves the identity, and the member
 * who owns it takes over this new, empty login. The old login's sessions end.
 */
function Relink({ secretKey, ownerName }: { secretKey: Uint8Array; ownerName: string }) {
  const t = useT();
  const { refresh, setPendingKey } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function relink() {
    setError(null);
    setBusy(true);
    try {
      await relinkLogin(secretKey);
      // The session now belongs to the member who owns the identity; /restore protects the key on this device.
      setPendingKey(secretKey);
      await refresh();
      navigate("/restore");
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <p>{t.onboarding.relinkText(ownerName)}</p>
      <button className="primary" disabled={busy} onClick={relink}>
        {busy ? t.onboarding.relinking : t.onboarding.relink}
      </button>
      {error && <p className="error">{error}</p>}
    </div>
  );
}

function ShowPhrase({ phrase, onNext }: { phrase: string; onNext: () => void }) {
  const t = useT();
  const [written, setWritten] = useState(false);
  return (
    <>
      <h2>{t.onboarding.phraseTitle}</h2>
      <p>{t.onboarding.writeDown}</p>
      <ol className="phrase">
        {phraseWords(phrase).map((w, i) => (
          <li key={i}>{w}</li>
        ))}
      </ol>
      <p className="warn">{t.onboarding.whoeverHas}</p>
      <label className="radio">
        <input type="checkbox" checked={written} onChange={(e) => setWritten(e.target.checked)} /> {t.onboarding.wrote}
      </label>
      <button className="primary" disabled={!written} onClick={onNext}>
        {t.common.continue}
      </button>
    </>
  );
}

function ConfirmPhrase({ phrase, onBack, onNext }: { phrase: string; onBack: () => void; onNext: () => void }) {
  const t = useT();
  const words = phraseWords(phrase);
  const checks = useMemo(pickChecks, []);
  const [typed, setTyped] = useState<Record<number, string>>({});
  const ok = checks.every((i) => (typed[i] ?? "").trim().toLowerCase() === words[i]);

  return (
    <>
      <h2>{t.onboarding.checkTitle}</h2>
      <p>{t.onboarding.checkText}</p>
      <div className="fields">
        {checks.map((i) => (
          <label key={i}>
            {t.onboarding.word(i + 1)}
            <input autoComplete="off" autoCapitalize="none" spellCheck={false} value={typed[i] ?? ""} onChange={(e) => setTyped({ ...typed, [i]: e.target.value })} />
          </label>
        ))}
      </div>
      <button className="primary" disabled={!ok} onClick={onNext}>
        {t.common.continue}
      </button>{" "}
      <button className="link" onClick={onBack}>
        {t.onboarding.showAgain}
      </button>
    </>
  );
}
